import { type MouseEvent, useState } from 'react';
import { useGalleryChildrenDropMonitor } from '@/hooks/dragDrop/useGalleryChildrenDropMonitor';
import { useSortedFilteredChildren } from '@/hooks/data/tree/useSortedFilteredChildren';
import { GalleryChildChip } from '@/components/browser/gallery-child-chip';
import { type SortOrderOptions, type SortOrderValues } from '@/types/sort';
import { GalleryChildRow } from '@/components/browser/gallery-child-row';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { type ChildTreeNode, toSelectedNode } from '@/lib/child-node';
import { useDamNavigation } from '@/hooks/useDamNavigation';
import { Button } from '@/components/ui/button';
import { GalleryView } from '@/types';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

/** Children beyond this render only once the expander is used. */
const CHILDREN_CAP = 12;

export type ChildrenSectionProps = {
  /** Not named `children` — this is data, not slot content. */
  nodes: Array<ChildTreeNode>;
  galleryView: GalleryView;
  sortValue?: SortOrderValues;
  sortOrder?: SortOrderOptions;
  queryString?: string;
  /** Slugs of the folder being browsed; a child's path is this plus the child's own slug. */
  pathSlugs: Array<string>;
  /**
   * Ids of the asset table's visible leaf columns, in render order. One fact, from which both the
   * caption rows' colSpan and each child row's cell layout are derived — previously the layout
   * computed a count and a Name index here and the row re-derived which cell held the icon from
   * the index alone.
   */
  visibleColumnIds: Array<string>;
  /**
   * Whether an assets no-results state is being shown. Gating only — the state itself
   * is rendered by GalleryLayout outside the table, because a `<td>` in a `table-fixed` table wider
   * than the viewport cannot be centred in the view.
   *
   * It decides which of two empty states speaks: when nothing matched at all, the caller's generic
   * one covers the whole page and this section stands down entirely, captions included.
   */
  assetsNoResults?: boolean;
  /**
   * The band's own visibility setting (DM-2518), off. `nodes` still holds the real children — a
   * folder with hidden children is not an empty one — so this swaps the caption's contents for a
   * notice instead of the chips/rows, rather than the caller falling through to the page-level
   * empty state.
   */
  hidden?: boolean;
  /**
   * The currently browsed folder's own id and full name path, forwarded to each child's context
   * menu for the upload items (DM-2518 follow-up). Without it, "Upload" on a child rendered only
   * here has no reliable node cache to read a path from — see `ChildNodeMenuItems`.
   */
  parentPath?: { id: string; namePath: Array<string> };
  /**
   * Runs before the click toggles selection, so GalleryLayout can decide whether showing the
   * selection bar would cover the clicked row — the same measure-and-defer decision an asset row's
   * click makes (DM-1894), now reused here instead of child rows toggling selection with no regard
   * for the bar at all.
   */
  onRowClick?: (childId: string, event?: MouseEvent<HTMLElement>) => void;
};

/**
 * The browser view's leading section: a folder's child folders and albums above its assets, in
 * both tile and list view. Rendered through DataTableGridListView's leading slots so it scrolls
 * with the assets and stays outside the asset row model — which is what makes "not draggable",
 * "not swept up by select-all" and "unaffected by pagination" structural rather than suppressed.
 */
export const ChildrenSection = ({
  nodes,
  galleryView,
  sortValue,
  sortOrder,
  queryString,
  pathSlugs,
  visibleColumnIds,
  assetsNoResults = false,
  hidden = false,
  parentPath,
  onRowClick,
}: ChildrenSectionProps) => {
  const tBrowser = useTranslations('browser');
  const tCommon = useTranslations('common');
  const [expanded, setExpanded] = useState(false);
  const { selection, toggleNodeItemSelection, contextNodeItemSelection } = useGallerySelection();
  const { navigateToNode } = useDamNavigation();

  const sorted = useSortedFilteredChildren(nodes, { sortValue, sortOrder, queryString });
  // The cap applies after filtering, so a filtered-down set never shows a pointless expander.
  const visible = expanded ? sorted : sorted.slice(0, CHILDREN_CAP);

  const selectedChildId = selection.kind === 'nodes' ? selection.item.id : undefined;

  // One handler for every row, rather than the whole mutation and translation stack per row.
  useGalleryChildrenDropMonitor(visible.map(toSelectedNode));

  const handleSelect = (child: ChildTreeNode, event?: MouseEvent<HTMLElement>) => {
    onRowClick?.(child.node.id, event);
    toggleNodeItemSelection(toSelectedNode(child));
  };

  // Right-click selects what you right-clicked, exactly as it does for an asset row
  // (GalleryLayout's handleRightClick → contextItemSelection). Never a toggle: opening the menu on
  // the already-selected child must not deselect it and leave the menu acting on nothing.
  const handleContextMenu = (child: ChildTreeNode) => contextNodeItemSelection(toSelectedNode(child));

  // One entry point for both kinds. navigateToNode clears the gallery selection, select-all,
  // locateAssetId and the asset pagination itself — the album branch used to call
  // browseRedirection directly and skip all of that.
  const handleEnter = (child: ChildTreeNode) => {
    void navigateToNode(child.node.id, child.node.name, child.type, [...pathSlugs, child.node.slug]);
  };

  // No children at all: the folder simply has none, so render nothing (distinct from the
  // "filtered everything out" state below).
  if (nodes.length === 0) {
    return null;
  }

  const colSpan = visibleColumnIds.length;

  // h-6 rather than h-8, and the tile wrapper's padding is trimmed to match: the captions were
  // taller than the content they label. The horizontal inset is per view, because the two surfaces
  // align to different things — the name-column parity depends on the cells, not on this.
  const captionClassName = 'flex h-6 items-center text-xs font-bold tracking-wide uppercase text-text-gray';
  // List view: px-4 is what keeps the caption lined up with the table rows.
  const listCaptionClassName = cn(captionClassName, 'px-4');

  /*
   * Always "Folders & albums", whichever kinds happen to be loaded — including above the no-match
   * line, and including a folder that currently holds only subfolders.
   *
   * This is a section label, not a description of the current contents: it tells you what belongs
   * here, which is where an album will appear the moment one is added. Customers navigate by it, so
   * it has to sit in the same place saying the same thing every time. A caption that renamed itself
   * to "Folders" or "Albums" per folder was briefly tried and reverted for that reason — the
   * churn cost more than the redundancy it removed.
   */
  const childrenCaption = tBrowser('childrenCaption');

  // Mutually exclusive and sharing a guard, so one button rather than two blocks per view.
  const showToggle = sorted.length > CHILDREN_CAP;
  const toggleLabel = expanded ? tCommon('showLess') : tBrowser('showAll', { count: sorted.length });
  // Button, not a plain <button> — it needs the shared focus-visible treatment (darker
  // background, no ring), which a bare button doesn't get.
  const toggleClassName = 'h-auto min-h-0 p-0 text-xs font-bold text-highlight-base dark:text-highlight-base';

  /*
   * Exactly one empty state, always — but the right one.
   *
   * A filter matching neither a child nor an asset used to show only "No folders or albums match",
   * which is a statement about half the page: the children keep GalleryLayout out of its own
   * whole-page empty state, so the assets side said nothing at all and the reader could not tell
   * whether assets had been searched. Now the generic no-results state speaks for the whole page in
   * that case, and the folders/albums-specific line stands down — it would be a second empty state
   * saying strictly less.
   *
   * The two partial cases are unchanged: children matched but no assets → chips then the assets
   * no-results; no children matched but assets follow → the folders/albums line then the assets.
   */
  const showChildrenEmpty = sorted.length === 0 && !assetsNoResults;

  /*
   * The caption labels the children region, so it needs a region to label.
   *
   * When nothing matches at all it has neither rows nor the no-match line under it — the generic
   * no-results state took that job — and a bare "FOLDERS & ALBUMS" sat directly above a message
   * about assets. That is precisely the "heading over nothing" the assets caption below is gated to
   * avoid; the rule just wasn't applied to both captions. The stable-section-label argument above
   * still holds for every case where the region exists, including above the no-match line.
   */
  const showChildrenCaption = sorted.length > 0 || showChildrenEmpty;

  // In list view the caption goes in an inner div, never on the <td> itself: the table is
  // `table-fixed`, and making a spanning cell a flex container collapses it to the first
  // column's width so the caption wraps and clips. Same shape as ingestion-layout's group rows.
  //
  // `role=heading` on that inner div: the two regions are what a reader navigates this view by, and
  // uppercase bold text is a heading to a sighted user and nothing at all to anyone else. A <td>
  // may contain a heading, so this costs no layout and no styling.
  const captionRow = (cy: string, label: string) => (
    <tr>
      <td colSpan={colSpan} className="p-0" data-cy={cy}>
        <div className={listCaptionClassName} role="heading" aria-level={2}>
          {label}
        </div>
      </td>
    </tr>
  );

  // The band is hidden: no caption, no notice, Assets included — the View menu already says so.
  if (hidden) {
    return null;
  }

  if (galleryView === GalleryView.LIST) {
    return (
      <>
        {showChildrenCaption && captionRow('children-section-caption', childrenCaption)}
        {sorted.length === 0 ? (
          showChildrenEmpty && (
            <tr>
              <td colSpan={colSpan} className="px-4 py-2 text-sm text-text-gray" data-cy="children-section-empty">
                {tBrowser('noChildrenMatch')}
              </td>
            </tr>
          )
        ) : (
          <>
            {visible.map((child) => (
              <GalleryChildRow
                key={child.id}
                node={child}
                visibleColumnIds={visibleColumnIds}
                selected={selectedChildId === child.node.id}
                onClick={(event) => handleSelect(child, event)}
                onDoubleClick={() => handleEnter(child)}
                onContextMenu={() => handleContextMenu(child)}
                parentPath={parentPath}
              />
            ))}
            {showToggle && (
              <tr>
                <td colSpan={colSpan} className="px-4 py-2">
                  <Button
                    type="button"
                    variant="link"
                    data-cy="children-section-expander"
                    aria-expanded={expanded}
                    className={toggleClassName}
                    onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
                  >
                    {toggleLabel}
                  </Button>
                </td>
              </tr>
            )}
          </>
        )}
        {showChildrenCaption && captionRow('assets-section-caption', tBrowser('assetsCaption'))}
      </>
    );
  }

  // The grid container's own p-6 already insets this block, so the only edge left to match is the
  // asset tile's own p-1.5 — that is what puts the chips and captions on the tiles' content edge.
  return (
    <div className="col-span-full flex flex-col gap-y-1 px-1.5 pt-2">
      {showChildrenCaption && (
        <div className={captionClassName} data-cy="children-section-caption" role="heading" aria-level={2}>
          {childrenCaption}
        </div>
      )}
      {sorted.length === 0 ? (
        showChildrenEmpty && (
          <div className="px-1 pb-2 text-sm text-text-gray" data-cy="children-section-empty">
            {tBrowser('noChildrenMatch')}
          </div>
        )
      ) : (
        <>
          <div className="flex flex-wrap gap-2 pb-2">
            {visible.map((child) => (
              <GalleryChildChip
                key={child.id}
                node={child}
                selected={selectedChildId === child.node.id}
                onClick={() => handleSelect(child)}
                onDoubleClick={() => handleEnter(child)}
                onContextMenu={() => handleContextMenu(child)}
                parentPath={parentPath}
              />
            ))}
          </div>
          {showToggle && (
            <Button
              type="button"
              variant="link"
              data-cy="children-section-expander"
              aria-expanded={expanded}
              className={cn(toggleClassName, 'self-start')}
              onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
            >
              {toggleLabel}
            </Button>
          )}
        </>
      )}
      {showChildrenCaption && (
        <div className={captionClassName} data-cy="assets-section-caption" role="heading" aria-level={2}>
          {tBrowser('assetsCaption')}
        </div>
      )}
    </div>
  );
};
